import { useEffect, useState } from "react";

/**
 * `navigator.onLine` plus the `online`/`offline` events — the whole offline
 * mechanism the spec permits (Boundaries & Constraints: "offline detection
 * is `navigator.onLine` and the `online`/`offline` events, both standard
 * browser APIs" — no new dependency). Kept thin, the same way
 * `use-media-query.ts` wraps `matchMedia`: the one thing this hook decides
 * is "what does the browser currently say", nothing about what a pending
 * edit should do in response — that decision lives in
 * `lib/inline-edit.ts`'s pure `reduceCellEdit`/`cellsPendingReconnectRetry`.
 */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    function handleOnline() {
      setOnline(true);
    }
    function handleOffline() {
      setOnline(false);
    }
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return online;
}
